import React, {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  forwardRef,
  KeyboardEventHandler,
} from 'react';

import clsx from 'clsx';

import css from './index.module.css';
import Label from 'ui/Label';

export type InputTheme = 'light' | 'dark';
export type InputLayout = 'clean' | 'outline' | 'solid' | 'border-bottom' | 'editable';
export type InputType = 'text' | 'number' | 'email' | 'tel' | 'textarea';
export type InputSize = 'small' | 'normal';
export type InputAlign = 'left' | 'center' | 'right';

export type InputProps = {
  id?: string;
  min?: number;
  max?: number;
  type?: InputType;
  name?: string;
  errorMessage?: string;
  label?: string;
  areaLabel?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: string | number;
  color?: InputTheme;
  layout?: InputLayout;
  size?: InputSize;
  align?: InputAlign;
  isRightIcon?: boolean;
  isLeftIcon?: boolean;
  /** Use this to remove steppers on numberfields */
  removeControls?: boolean;
  isReadOnly?: boolean;
  dataTestId?: string;
  defaultValue?: string | number;
  errorBorderOnly?: boolean;
  maxLength?: number;
  icon?: () => React.ReactNode;
  onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onKeyUp?: KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onPaste?: ClipboardEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  rows?: number;
  labelClassName?: string;
  helpText?: string;
};

const Input = forwardRef(
  (
    {
      name,
      label,
      areaLabel = '',
      id,
      type = 'text',
      required,
      disabled = false,
      placeholder,
      value,
      color = 'light',
      layout = 'solid',
      size = 'normal',
      align = 'left',
      errorMessage = '',
      removeControls = false,
      isReadOnly = false,
      isLeftIcon = false,
      isRightIcon = false,
      dataTestId,
      defaultValue,
      errorBorderOnly = false,
      maxLength = -1,
      min,
      max,
      icon,
      onChange,
      onKeyUp,
      onKeyDown,
      onPaste,
      onBlur,
      rows,
      labelClassName = '',
      helpText,
    }: InputProps,
    reference: React.Ref<any>,
  ) => {
    const inputClasses = clsx(
      icon ? css.hasIcon : null,
      css.element,
      css[color],
      css[layout],
      css[size],
      css[align],
      removeControls ? css.removeControls : null,
      { 'mt-1': !!label },
      { '!border-red': errorMessage },
      isLeftIcon ? '!pl-7' : null,
      isRightIcon ? '!pr-11' : null,
      isReadOnly ? 'cursor-not-allowed' : null,
      { 'min-h-[145px] resize-none': type === 'textarea' },
    );

    return (
      <div className="relative">
        {label ? (
          <Label htmlFor={id} customClass={labelClassName}>
            {label}
          </Label>
        ) : null}
        {label && helpText ? <p className="text-sm text-gray-500">{helpText}</p> : null}
        <div className="relative">
          {type === 'textarea' ? (
            <textarea
              className={inputClasses}
              id={id}
              name={name}
              required={required}
              disabled={disabled}
              placeholder={placeholder}
              readOnly={isReadOnly}
              value={value}
              defaultValue={defaultValue}
              ref={reference}
              data-testid={dataTestId}
              aria-label={areaLabel}
              onChange={onChange}
              onKeyUp={onKeyUp}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onBlur={onBlur}
              maxLength={maxLength}
              rows={rows}
            />
          ) : (
            <input
              className={inputClasses}
              id={id}
              name={name}
              type={type}
              required={required}
              disabled={disabled}
              placeholder={placeholder}
              readOnly={isReadOnly}
              value={value}
              defaultValue={defaultValue}
              ref={reference}
              data-testid={dataTestId}
              aria-label={areaLabel}
              onChange={onChange}
              onKeyUp={onKeyUp}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onBlur={onBlur}
              min={type === 'number' && typeof min !== 'undefined' ? min : undefined}
              max={type === 'number' && typeof max !== 'undefined' ? max : undefined}
              maxLength={maxLength}
            />
          )}
          {icon ? (
            <span className="absolute bottom-0 top-0 text-black/40 left-3 medium:left-4 text-3xl pointer-events-none flex items-center">
              <>{icon()}</>
            </span>
          ) : null}
        </div>
        {errorMessage && !errorBorderOnly ? <p className="text-red mt-2">{errorMessage}</p> : null}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
